// traceability: requirements: [NFR-008]
import * as RadixToast from '@radix-ui/react-toast';
import { clsx } from 'clsx';
import { useNotificationStore } from '@/stores/notificationStore';
import type { NotificationType } from '@/stores/notificationStore';

const typeStyles: Record<NotificationType, string> = {
  success: 'border-jade-400 bg-jade-50 text-jade-800 dark:bg-jade-900/30 dark:text-jade-300 dark:border-jade-700',
  error: 'border-cinnabar-400 bg-cinnabar-50 text-cinnabar-800 dark:bg-cinnabar-900/30 dark:text-cinnabar-300 dark:border-cinnabar-700',
  warning: 'border-gold-400 bg-gold-50 text-gold-800 dark:bg-gold-900/30 dark:text-gold-300 dark:border-gold-700',
  info: 'border-blue-400 bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
};

const typeIcons: Record<NotificationType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

export function ToastProvider() {
  const { notifications, remove } = useNotificationStore();

  return (
    <RadixToast.Provider swipeDirection="right" duration={5000}>
      {notifications.map((notification) => (
        <RadixToast.Root
          key={notification.id}
          className={clsx(
            'flex items-start gap-3 rounded-lg border p-4 shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full',
            typeStyles[notification.type],
          )}
          onOpenChange={(open) => {
            if (!open) remove(notification.id);
          }}
        >
          <span className="mt-0.5 text-lg leading-none" aria-hidden="true">
            {typeIcons[notification.type]}
          </span>
          <div className="flex-1">
            <RadixToast.Title className="text-sm font-semibold">
              {notification.title}
            </RadixToast.Title>
            {notification.message && (
              <RadixToast.Description className="mt-1 text-sm opacity-80">
                {notification.message}
              </RadixToast.Description>
            )}
          </div>
          <RadixToast.Close
            className="shrink-0 rounded p-1 opacity-60 hover:opacity-100 focus:outline-none focus-visible:ring-2"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </RadixToast.Close>
        </RadixToast.Root>
      ))}
      <RadixToast.Viewport className="fixed end-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2" />
    </RadixToast.Provider>
  );
}
